import { Avatar, Box, Button, Card, CardActions, CardContent, CardHeader, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, List, ListItem, ListItemAvatar, ListItemText, makeStyles, Theme, Typography } from "@material-ui/core";
import { blue, green } from "@material-ui/core/colors";
import Snackbar from '@material-ui/core/Snackbar';
import UserIcon from '@material-ui/icons/AccountCircleOutlined';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import AssignmentOutlinedIcon from '@material-ui/icons/AssignmentOutlined';
import BusinessIcon from '@material-ui/icons/Business';
import NfcIcon from '@material-ui/icons/ContactlessOutlined';
import KeyboardCapslockIcon from '@material-ui/icons/KeyboardCapslock';
import LastPageIcon from '@material-ui/icons/LastPage';
import NfcWriteIcon from '@material-ui/icons/PhonelinkRingOutlined';
import MuiAlert from '@material-ui/lab/Alert';
import { Omit } from '@material-ui/types';
import React, { useEffect, useState } from "react";
import { Link as RouterLink, LinkProps as RouterLinkProps } from 'react-router-dom';
import { useCheckPermissionQuery, useGetNfcKeyMutation, useGetWebPubSubClientAccessUrlMutation, useSaveNfcKeyMutation } from "../../graphql/generated/graphql";
import { useCacheEvict } from "../../shared/Hooks/useCacheEvict";
import PermissionKeys from "../../shared/PermissionKeys";

const useStyles = makeStyles((theme: Theme) => ({
  layoutRoot: {
    marginTop: theme.spacing(2),
  },
  card: {
    marginBottom: theme.spacing(1),
  },
  warehouseAvatar: {
    backgroundColor: green[500],
  },
  warehouseItemAvatar: {
    // backgroundColor: green[800],
    color: theme.palette.action.active,
  },
  nfcAvatar: {
    backgroundColor: blue[500],
    color: theme.palette.getContrastText(blue[500]),
  },
  demoAvatar: {
    backgroundColor: blue[500],
  },
}));

interface ListItemLinkProps
{
  icon?: React.ReactElement;
  primary: string;
  to: string;
}

function ListItemLink(props: ListItemLinkProps)
{
  const { icon, primary, to } = props;
  const classes = useStyles();

  const renderLink = React.useMemo(
    () =>
      React.forwardRef<any, Omit<RouterLinkProps, 'to'>>((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to],
  );

  return (
    <li>
      <ListItem button component={renderLink}>
        {icon ? <ListItemAvatar><Avatar className={classes.warehouseItemAvatar}>{icon}</Avatar></ListItemAvatar> : null}
        <ListItemText primary={primary} />
      </ListItem>
    </li>
  );
}

export default function Component()
{
  const classes = useStyles();

  const { data: checkInventoryWritePermissionData } = useCheckPermissionQuery({ variables: { PermissionKey: PermissionKeys.Warehouse.Inventory.Write } });

  const [isNFC, setIsNFC] = useState(false);

  useEffect(() =>
  {
    if('NDEFReader' in window)
    {
      setIsNFC(true);
    }
  }, []);

  const [isNFCWriteDialogOpen, setIsNFCWriteDialogOpen] = useState(false);
  const [getNFCKey] = useGetNfcKeyMutation();
  const [saveNFCKey] = useSaveNfcKeyMutation();

  const showNfcWriteDialog = () =>
  {
    setIsNFCWriteDialogOpen(true);

  };

  const [snackBarMessage, setSnackBarMessage] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('info');
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  useEffect(() =>
  {
    let isMounted = true;
    async function writeNfc()
    {
      setSnackBarMessage(null);
      const controller = new AbortController();
      setAbortController(controller);
      if('NDEFReader' in window)
      {
        try
        {
          const ndef = new (window as any).NDEFReader();
          const nfcKey = await getNFCKey();
          await ndef.write({ records: [{ recordType: "url", data: `${window.location.origin}/#/nfc/${encodeURIComponent(nfcKey.data?.GetNFCKey!)}` }] }, { signal: controller.signal });
          if(!isMounted) return;
          await saveNFCKey({ variables: { NFCKey: nfcKey.data?.GetNFCKey! } });
          setSnackBarMessage("Sikeres NFC írás!");
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
          setIsNFCWriteDialogOpen(false);
        }
        catch(err: any)
        {
          if(!isMounted) return;
          if(err?.name === 'AbortError')
          {
            setSnackBarMessage("Az NFC írás megszakítva.");
            setSnackbarSeverity('info');
          } else
          {
            setSnackBarMessage("Hiba az NFC írás során: " + (err?.message || err));
            setSnackbarSeverity('error');
          }
          setSnackbarOpen(true);
          setIsNFCWriteDialogOpen(false);
        }
        finally
        {
          setAbortController(null);
        }
      }
      else
      {
        setSnackBarMessage("Ez az eszköz nem támogatja az NFC írást.");
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setIsNFCWriteDialogOpen(false);
        setAbortController(null);
      }
    }
    if(isNFCWriteDialogOpen) writeNfc();
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNFCWriteDialogOpen]);

  const handleCancelNfcWrite = () =>
  {
    if(abortController)
    {
      abortController.abort();
    }
    setIsNFCWriteDialogOpen(false);
  };

  const handleSnackbarClose = (_event?: React.SyntheticEvent, reason?: string) =>
  {
    if(reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  const [userDisplayName] = useState<string | null>(() => localStorage.getItem("userDisplayName"));

  const [getClientAccessUrl] = useGetWebPubSubClientAccessUrlMutation();
  useCacheEvict({
    getClientAccessUrl: async () => (await getClientAccessUrl()).data?.GetWebPubSubClientAccessUrl!,
    groupName: "executable-receipts",
    queryName: "ListReceipts",
  });

  return (
    <Box className={classes.layoutRoot}>
      <Card className={classes.card}>
        <CardHeader title="Raktár" subheader="Áruraktározási funkciók" avatar={<Avatar className={classes.warehouseAvatar} >R</Avatar>} />
        <CardContent>
          <List component="nav" aria-label="main mailbox folders">
            <ListItemLink to="/receipts/1" primary="Áruátvétel" icon={<LastPageIcon />} />
            <ListItemLink to="/receipts/2" primary="Árukiadás" icon={<KeyboardCapslockIcon />} />
            <ListItemLink to="/receipts" primary="Raktározás" icon={<BusinessIcon />} />
            {checkInventoryWritePermissionData?.CheckPermission ? <ListItemLink to="/inventories" primary="Leltár" icon={<AssignmentOutlinedIcon />} /> : null}
          </List>
        </CardContent>
        {userDisplayName ? <CardActions disableSpacing style={{ paddingTop: 0, paddingLeft: 16 }}>
          <Box display="flex" alignItems="center" style={{ marginBottom: 8 }}>
            <UserIcon />
            <Typography variant="body2" component="span" style={{ marginLeft: 8 }}>
              {userDisplayName}
            </Typography>
          </Box>
        </CardActions>
          : null}
      </Card>
      {isNFC ? <Card className={classes.card}>
        <CardHeader style={{ paddingBottom: 0 }} title="NFC" subheader="NFC kulcs" avatar={<Avatar className={classes.nfcAvatar}><NfcIcon /></Avatar>} />
        <CardContent style={{ paddingTop: 0, paddingBottom: 4 }}>
          <List component="nav" aria-label="main mailbox folders">
            <ListItem button onClick={() => showNfcWriteDialog()}>
              <ListItemAvatar>
                <Avatar className={classes.warehouseItemAvatar}><NfcWriteIcon /></Avatar>
              </ListItemAvatar>
              <ListItemText primary="NFC kulcs írása" />
            </ListItem>
          </List>
        </CardContent>
      </Card>
        : null}

      <Card>
        <CardHeader style={{ paddingBottom: 0 }} title="Demo" subheader="Vonalkód beolvasás" avatar={<Avatar className={classes.demoAvatar}>D</Avatar>} />
        <CardContent style={{ paddingTop: 16, paddingBottom: 0 }}>
          <Typography variant="body2" component="p">Próbáld ki a "Vonalkód demo"-t</Typography>
        </CardContent>
        <CardActions disableSpacing style={{ paddingTop: 0 }}>
          <IconButton component={RouterLink} to="/barcodedemo" title="Vonalkód Demo"><ArrowForwardIcon /></IconButton>
        </CardActions>
      </Card>

      {isNFCWriteDialogOpen ? (
        <Dialog open={isNFCWriteDialogOpen} onClose={handleCancelNfcWrite}>
          <DialogTitle>NFC kulcs írása</DialogTitle>
          <DialogContent>
            <DialogContentText>
              <Box display="flex" alignItems="flex-start">
                <Box mr={1}>
                  <NfcWriteIcon style={{ fontSize: 48, color: blue[500] }} />
                </Box>
                <Box>
                  Kérlek, tartsd az NFC kulcsot a telefonhoz, és várd meg, amíg a rendszer kiírja a szükséges adatokat.
                </Box>
              </Box>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelNfcWrite} color="primary" disabled={!abortController}>
              Mégse
            </Button>
          </DialogActions>
        </Dialog>) : null}

      <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <MuiAlert elevation={6} variant="filled" onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackBarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
